<template>
  <div class="form-item-container mb-6">
    <v-tooltip v-if="!userCanEditFormItems" activator="parent" location="center">
      {{ $t('i18n.editNotAvailable') }}
    </v-tooltip>
    <div class="fixed-content-container">
      <div class="header pb-2">
        <div class="header-title">
          <v-icon class="item-icon cursor-move" icon="mdi-drag-vertical" size="large"></v-icon>
          <div class="font-weight-black">{{ $t('i18n.textInputTitle') }}</div>
        </div>
        <div class="header-tool-icons">
          <v-btn class="cursor-pointer" variant="text" icon @click="duplicateItem" :disabled="!userCanEditFormItems">
            <v-icon icon="$gorateDuplicate"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.duplicate') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="removeItem" :disabled="!userCanEditFormItems">
            <v-icon size="small" icon="$gorateDeleteOutline"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.remove') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="expandGrid" :disabled="!userCanEditFormItems">
            <v-icon :icon="expandGridButtonIconName" size="large"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.collapse') }}
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </div>
    <div ref="collapsableContentContainer" class="collapsable-content-container">
      <div style="overflow: hidden;">
        <div class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language1') }}</div>
          <v-text-field v-model="itemData.question_lang1" density="compact" :placeholder="$t('i18n.questionLang1')"
            variant="outlined" dense hide-details="auto" clearable counter :rules="questionRules"
            :disabled="!userCanEditFormItems"></v-text-field>
        </div>
        <div v-if="bilingual" class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language2') }}</div>
          <v-text-field v-model="itemData.question_lang2" density="compact" :placeholder="$t('i18n.questionLang2')"
            variant="outlined" dense hide-details="auto" clearable counter :rules="questionRules"></v-text-field>
        </div>
        <div class="body">
          <div></div>
          <v-textarea class="pt-1" :label="$t('i18n.userAnswerLabel')" variant="outlined" disabled rows="3"
            counter></v-textarea>
        </div>
        <div class="alert-container">
          <div></div>
          <v-alert class="mt-3 mb-3 alert" variant="outlined" type="warning" dense closable>
            {{ $t('i18n.alertTablets') }}
          </v-alert>
        </div>
      </div>
    </div>
  </div>
  <v-divider :thickness="2" class="border-opacity-100 mb-3" color="grey"></v-divider>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue'
import { FormDeviceAssociation, FormItem } from '@/utils/DataTypes'

export default defineComponent({
  name: 'TextInput',
  setup () {
    const collapsableContentContainer = ref<HTMLElement | null>(null)
    const isExpanded = ref(true)

    const expandGrid = () => {
      if (collapsableContentContainer.value) {
        isExpanded.value = !isExpanded.value
        collapsableContentContainer.value.style.gridTemplateRows = isExpanded.value ? '1fr' : '0fr'
      }
    }

    return {
      collapsableContentContainer,
      isExpanded,
      expandGrid
    }
  },
  emits: ['duplicate-item', 'remove-item', 'form-item-update'],
  props: {
    formList: {
      type: Array as PropType<FormDeviceAssociation[]>,
      required: true,
      default: () => []
    },
    indexInTheSketchForm: {
      type: Number,
      required: true
    },
    bilingual: {
      type: Boolean,
      required: false,
      default: false
    },
    formItems: {
      type: Array as PropType<FormItem[]>,
      required: false,
      default: null
    },
    userCanEditFormItems: {
      type: Boolean,
      required: true,
      default: true
    }
  },
  components: {
  },
  data () {
    return {
      itemData: {
        name: 'TEXT_INPUT',
        item_type_id: 2,
        question_lang1: '',
        question_lang2: '',
        required: true,
        bilingual: this.bilingual
      } as FormItem
    }
  },
  watch: {
    formItems: {
      handler () {
        if (this.formItems) {
          if (this.formItems[this.indexInTheSketchForm] !== undefined) {
            this.itemData = this.formItems[this.indexInTheSketchForm]
          }
        }
      },
      immediate: true
    },
    itemData: {
      handler (newItemData) {
        this.$emit('form-item-update', { formItemIndex: this.indexInTheSketchForm, data: newItemData })
      },
      deep: true
    },
    bilingual: {
      handler (newBilingual) {
        this.itemData.bilingual = newBilingual
        if (!newBilingual) {
          this.itemData.question_lang2 = ''
        }
      },
      immediate: true
    }
  },
  methods: {
    duplicateItem () {
      this.$emit('duplicate-item', this.indexInTheSketchForm)
    },
    removeItem () {
      this.$emit('remove-item', this.indexInTheSketchForm)
    }
  },
  computed: {
    questionRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.questionRequired')
        }, (value: string) => {
          if (value && value.length <= 180) return true
          return this.$t('i18n.questionMaxLength', { max: 180 })
        }
      ]
    },
    expandGridButtonIconName (): string {
      if (this.isExpanded) return 'mdi-chevron-up'
      else return 'mdi-chevron-down'
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.form-item-container {

  .body {
    display: grid;
    grid-template-columns: 6.5em 1fr;
    align-items: center;
  }

  .fixed-content-container {
    .header {
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr 1fr;
      align-items: center;

      .header-title {
        grid-column: 1;
        display: grid;
        grid-template-columns: auto auto;
        justify-self: start;
        align-items: center;
      }

      .header-tool-icons {
        grid-column: 2;
        justify-self: end;
      }

    }
  }

  .collapsable-content-container {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 300ms;

    .alert-container {
      display: grid;
      grid-template-columns: 6.5em 1fr;
      align-items: center;
    }
  }
}
</style>
